import React, { useEffect, useState } from "react";
import { Star, ArrowRight } from "lucide-react";
import { publicApi } from "../lib/api";
import { ReviewCard } from "./GoogleReviewsCarousel";

// Reusable "What our clients say on Google" section for real Google reviews.
// `offset` rotates which reviews show first (used to vary content across pages).
export default function GoogleReviewsSection({
  offset = 0,
  count = 3,
  excludeAuthor = "",
  eyebrow = "Trusted by local businesses",
  title = "What our clients say on Google",
  dark = false,
  testId = "google-reviews-section",
}) {
  const [rating, setRating] = useState(null);
  useEffect(() => { publicApi.googleRating().then(setRating).catch(() => {}); }, []);

  // Skip a review that is already featured verbatim higher up the page, so the
  // same quote never appears twice on one URL.
  const all = (rating?.reviews || []).filter((r) => !excludeAuthor || r.author !== excludeAuthor);
  const reviewsLink = rating?.url || rating?.write_review_url;

  // If reviews ever fail to load, show a lightweight link fallback rather than
  // an empty gap — but only once we know there genuinely are none to show.
  if (all.length === 0) {
    if (!rating || !reviewsLink) return null;
    return (
      <section className={dark ? "bg-navy text-white py-12" : "bg-slate-50 py-12"} data-testid={testId}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-2">
            <span className="flex gap-0.5">{[1, 2, 3, 4, 5].map((i) => <Star key={i} size={16} className={rating.rating && i <= Math.round(rating.rating) ? "fill-orange text-orange" : "fill-slate-300 text-slate-300"} />)}</span>
            <span className={`text-sm font-semibold ${dark ? "text-slate-200" : "text-slate-600"}`}>{rating.rating ? `${rating.rating} out of 5` : "Rated on Google"}{rating.total ? ` · ${rating.total} reviews` : ""}</span>
          </div>
          <a href={reviewsLink} target="_blank" rel="noopener noreferrer" data-testid={`${testId}-link`} className="inline-flex items-center gap-2 text-orange font-bold hover:underline">Read our Google reviews <ArrowRight size={16} /></a>
        </div>
      </section>
    );
  }

  const start = ((offset % all.length) + all.length) % all.length;
  const picks = [...all.slice(start), ...all.slice(0, start)].slice(0, count);

  return (
    <section className={dark ? "bg-navy text-white py-16 md:py-20" : "bg-slate-50 py-16 md:py-20"} data-testid={testId}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
          <div>
            <span className="text-xs font-bold uppercase tracking-widest text-orange">{eyebrow}</span>
            <h2 className={`text-2xl md:text-3xl font-extrabold leading-tight mt-1 font-display ${dark ? "text-white" : "text-navy"}`}>{title}</h2>
          </div>
          {rating?.rating && (
            <div className={`flex items-center gap-2 text-sm font-semibold ${dark ? "text-slate-200" : "text-slate-500"}`}>
              <span className="flex gap-0.5">{[1, 2, 3, 4, 5].map((i) => <Star key={i} size={16} className={i <= Math.round(rating.rating) ? "fill-orange text-orange" : "fill-slate-200 text-slate-200"} />)}</span>
              {rating.rating} out of 5 · {rating.total} reviews
            </div>
          )}
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {picks.map((r, i) => <ReviewCard key={i} r={r} />)}
        </div>
        {reviewsLink && (
          <div className="mt-8">
            <a href={reviewsLink} target="_blank" rel="noopener noreferrer" data-testid={`${testId}-link`} className="inline-flex items-center gap-2 text-orange font-bold hover:underline">Read all our Google reviews <ArrowRight size={16} /></a>
          </div>
        )}
      </div>
    </section>
  );
}
